import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Building, Upload, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface JobOpening {
  id: string;
  title: string;
  location: string;
  department: string;
  employment_type: string;
  description: string;
  requirements: string;
  benefits: string;
}

interface JobApplicationDialogProps {
  job: JobOpening;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const applicationSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().optional(),
  coverLetter: z.string().min(50, 'Cover letter must be at least 50 characters'),
});

type ApplicationFormData = z.infer<typeof applicationSchema>;

const JobApplicationDialog: React.FC<JobApplicationDialogProps> = ({
  job,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const { t } = useTranslation(['careers', 'common']);
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);

  const form = useForm<ApplicationFormData>({
    resolver: zodResolver(applicationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      coverLetter: '',
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file type
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: t('careers:application.invalidFileType'),
          description: t('careers:application.allowedFileTypes'),
          variant: 'destructive',
        });
        return;
      }

      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: t('careers:application.fileTooLarge'),
          description: t('careers:application.maxFileSize'),
          variant: 'destructive',
        });
        return;
      }

      setResumeFile(file);
    }
  };

  const removeFile = () => {
    setResumeFile(null);
    const fileInput = document.getElementById('resume-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const uploadResume = async (file: File): Promise<{ url: string; fileName: string }> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `resumes/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('job-applications')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('job-applications')
      .getPublicUrl(filePath);

    return { url: publicUrl, fileName: file.name };
  };

  const onSubmit = async (data: ApplicationFormData) => {
    if (!resumeFile) {
      toast({
        title: t('careers:application.resumeRequired'),
        description: t('careers:application.pleaseUploadResume'),
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload resume
      const { url: resumeUrl, fileName } = await uploadResume(resumeFile);

      // Submit application
      const { error } = await supabase
        .from('job_applications')
        .insert({
          job_opening_id: job.id,
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          phone: data.phone || null,
          cover_letter: data.coverLetter,
          resume_file_url: resumeUrl,
          resume_file_name: fileName,
        });

      if (error) throw error;

      toast({
        title: t('careers:application.submitted'),
        description: t('careers:application.submittedSuccess'),
      });

      onSuccess();
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: t('common:error'),
        description: t('careers:application.submitError'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex flex-col gap-2">
            <span>{t('careers:application.applyFor')} {job.title}</span>
            <div className="flex flex-wrap gap-2 text-sm font-normal">
              <Badge variant="outline" className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {job.location}
              </Badge>
              {job.department && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <Building className="w-3 h-3" />
                  {job.department}
                </Badge>
              )}
            </div>
          </DialogTitle>
          <DialogDescription>
            {t('careers:application.fillFormBelow')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('careers:application.firstName')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('careers:application.lastName')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('careers:application.email')}</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('careers:application.phone')} ({t('common:optional')})</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Resume Upload */}
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                {t('careers:application.resume')}
              </label>
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6">
                {resumeFile ? (
                  <div className="flex items-center justify-between p-3 bg-muted rounded-md">
                    <div className="flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      <span className="text-sm">{resumeFile.name}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={removeFile}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mb-2">
                      {t('careers:application.uploadResume')}
                    </p>
                    <p className="text-xs text-muted-foreground mb-4">
                      {t('careers:application.fileFormat')}
                    </p>
                    <input
                      id="resume-upload"
                      type="file"
                      accept=".pdf,.docx"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('resume-upload')?.click()}
                    >
                      {t('careers:application.chooseFile')}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Cover Letter */}
            <FormField
              control={form.control}
              name="coverLetter"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('careers:application.coverLetter')}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={6}
                      placeholder={t('careers:application.coverLetterPlaceholder')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {t('common:cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? t('careers:application.submitting') : t('careers:application.submitApplication')}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default JobApplicationDialog;